import type { LanguageModelExpectedIO, LanguageModelMessage } from "./global";

export type AIAvailability =
  | "unsupported" // LanguageModel global is missing
  | "requirements-unmet" // global exists but our system check fails
  | "unavailable" // browser reports unavailable (policy block, etc.)
  | "downloadable" // user can opt in to download
  | "downloading" // download is in flight
  | "available"; // ready to use

export interface SystemRequirementsReport {
  ok: boolean;
  // All optional: deviceMemory is non-standard, storage.estimate can throw, navigator may be undefined (tests).
  storageQuotaBytes?: number;
  deviceMemoryGb?: number;
  hardwareConcurrency?: number;
  warnings: string[];
}

export interface AISessionOptions {
  temperature?: number;
  topK?: number;
  initialPrompts?: LanguageModelMessage[];
  expectedInputs?: LanguageModelExpectedIO[];
  expectedOutputs?: LanguageModelExpectedIO[];
  signal?: AbortSignal;
}

export interface AIPromptOptions {
  signal?: AbortSignal;
  responseConstraint?: Record<string, unknown>;
}

export interface AISession {
  prompt(text: string, opts?: AIPromptOptions): Promise<string>;
  promptStreaming(text: string, opts?: { signal?: AbortSignal }): AsyncIterable<string>;
  destroy(): void;
}

export interface AIDownloadOptions {
  onProgress: (loaded: number, total?: number) => void;
  signal?: AbortSignal;
}

export interface AIProvider {
  // Free-form so future implementations (RemoteAIProvider, mock providers in tests)
  // can satisfy the interface — see docs/superpowers/specs/2026-05-18-on-device-ai-foundation-design.md.
  readonly id: string;
  checkAvailability(opts?: { languages?: string[] }): Promise<AIAvailability>;
  checkRequirements(): Promise<SystemRequirementsReport>;
  // User-gesture only; rejects with AISessionError (impls MUST wrap raw DOMExceptions).
  download(opts: AIDownloadOptions): Promise<void>;
  createSession(opts?: AISessionOptions): Promise<AISession>;
}

export class AISessionError extends Error {
  constructor(
    public readonly code:
      | "not-available"
      | "requirements-unmet"
      | "opted-out"
      | "aborted"
      | "parse-error"
      | "unknown",
    message: string
  ) {
    super(message);
    this.name = "AISessionError";
  }
}
